import React, { useState, useCallback, useEffect, useRef } from "react";
import ColoredTitle from "./ColoredTitle";
import { motion, AnimatePresence } from "framer-motion";
import GPDemo from "../../assets/GP/GPDemo.gif";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

const EXPERIENCES = [
  {
    period: "Sep 2026 — Present",
    title: "External Student Researcher",
    company: "NAIADEL Lab, University of Arizona",
    description:
      "Reproduced a multi-panel results figure from the lab's preprint in Python (pandas, matplotlib), cross-checking every calculation against the original R scripts until both matched exactly. Ran data quality checks on experiment data in R Markdown, worked through a Git pull-request workflow with GitHub Actions, wrote the lab's workflow guide, and delivered structured usability feedback on the lab's gamified research platform.",
    preview: null,
    previewUrl: null,
  },
  {
    period: "Mar 2026 — Present",
    title: "Open-Source Contributor",
    company: "Rocket.Chat",
    description:
      "Contributed to the AWS SDK v3 storage refactor (S3 client initialization) and benchmarked React Virtuoso vs. TanStack Virtual rendering 100k+ messages for long chats.",
    preview: null,
    previewUrl: null,
  },
  {
    period: "Nov 2025 — Aug 2026",
    title: "Software Engineer",
    company: "iOffer",
    description:
      "Built an LLM-based ingestion pipeline that turns unstructured resumes and essays into structured PostgreSQL records for downstream AI workflows. Developed the full-stack platform (React, Supabase) serving 300+ students and consulting agencies with multi-tenant data isolation, plus an internal dashboard tracking subscriptions, LLM token spend and engagement. Deployed a Node.js API gateway on AWS ECS Fargate with a job queue that cut peak-hour system overload by 80%.",
    preview: null,
    previewUrl: "https://i-offer.ai/",
  },
  {
    period: "May 2025 — Oct 2025",
    title: "Software Engineer",
    company: "Timing",
    description:
      "Shipped dependency-driven scheduling features end to end with product and design partners for a 100+ user beta cohort. Made sure concurrent updates across a multi-stage scheduling pipeline never overwrite user edits, validated with Zod schemas and property-based tests (Jest, fast-check).",
    preview: null,
    previewUrl: null,
  },
  {
    period: "Sep 2024 — Dec 2024",
    title: "Software Engineer Intern",
    company: "GreenPlatter",
    description:
      "Spearheaded the brand’s flagship marketing website and standardized its frontend infrastructure. Established strict API contracts and automated workflows that eliminated integration errors, significantly accelerating the production lifecycle for this AI health-tech platform.",
    preview: GPDemo,
    previewAlt: "GreenPlatter demo",
  },
];

const WARMED_PREVIEWS = new Set();

function injectResourceHint(rel, href) {
  if (!href || typeof document === "undefined") return;
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;

  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
}

function warmupPreview(exp) {
  if (!exp || WARMED_PREVIEWS.has(exp.title)) return;

  if (exp.preview) {
    const img = new Image();
    img.decoding = "async";
    img.src = exp.preview;
  }

  if (exp.previewUrl) {
    try {
      const origin = new URL(exp.previewUrl).origin;
      injectResourceHint("dns-prefetch", origin);
      injectResourceHint("preconnect", origin);
    } catch {
      // Ignore malformed preview URLs.
    }
  }

  WARMED_PREVIEWS.add(exp.title);
}

function BottomSheet({ exp, onClose }) {
  // Lock body scroll while sheet is open
  useEffect(() => {
    if (!exp) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [exp]);

  return (
    <AnimatePresence>
      {exp && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-[70] bg-lightBeige rounded-t-3xl
              px-6 pt-5 pb-12 overflow-y-auto"
            style={{ maxHeight: "78vh" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            {/* Drag handle */}
            <div className="w-10 h-1 bg-lightBrown/40 rounded-full mx-auto mb-6" />

            {/* Period */}
            <p className="text-xs font-semibold text-midBrown tracking-wide mb-1">
              {exp.period}
            </p>

            {/* Title + star */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-2xl sm:text-3xl">{exp.title}</h3>
              <span className="text-lightBrown text-base">✶</span>
            </div>

            {/* Company */}
            <p className="font-semibold italic text-brown text-lg mb-5">{exp.company}</p>

            {/* Description */}
            <p className="leading-relaxed text-midBrown text-sm sm:text-base">
              {exp.description}
            </p>

            {/* GIF preview on mobile */}
            {exp.preview && (
              <div className="mt-6">
                <GifPreview src={exp.preview} alt={exp.previewAlt} />
              </div>
            )}

            {/* Visit website link (no iframe on mobile) */}
            {exp.previewUrl && (
              <a
                href={exp.previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold
                  text-lightBrown border-b border-lightBrown/50 pb-0.5
                  hover:text-brown transition-colors"
              >
                Visit Website ↗
              </a>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function IframePreview({ url, title }) {
  const [loaded, setLoaded] = useState(false);
  const [shouldMountFrame, setShouldMountFrame] = useState(false);
  const handleLoad = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setShouldMountFrame(true);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [url]);

  return (
    <div className="w-full rounded-xl overflow-hidden shadow-md border border-deepBeige">
      {/* Mini browser bar */}
      <div className="bg-deepBeige px-3 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-brown/40" />
          <div className="w-3 h-3 rounded-full bg-brown/40" />
          <div className="w-3 h-3 rounded-full bg-brown/40" />
        </div>
        <span className="text-xs text-midBrown font-medium truncate flex-1 text-center pr-8">
          {url}
        </span>
      </div>

      {/* Skeleton shown while iframe loads */}
      <div className="relative">
        {!loaded && (
          <div className="absolute inset-0 bg-lightBeige animate-pulse z-10" />
        )}
        {shouldMountFrame && (
          <motion.iframe
            src={url}
            title={title}
            className="w-full h-64 sm:h-72 md:h-80 lg:h-96 border-0"
            loading="eager"
            sandbox="allow-scripts allow-same-origin"
            onLoad={handleLoad}
            initial={{ opacity: 0 }}
            animate={{ opacity: loaded ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        )}
      </div>
    </div>
  );
}

function GifPreview({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [shouldMountImage, setShouldMountImage] = useState(false);
  const handleLoad = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setShouldMountImage(true);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [src]);

  return (
    <div className="relative h-64 w-full overflow-hidden rounded-xl border border-deepBeige bg-lightBeige shadow-md sm:h-72 md:h-80 lg:h-96">
      {!loaded && (
        <div className="absolute inset-0 z-10 animate-pulse bg-lightBeige" />
      )}
      {shouldMountImage && (
        <motion.img
          src={src}
          alt={alt}
          loading="eager"
          onLoad={handleLoad}
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </div>
  );
}

export default function ExperienceSection() {
  const isMobile = useIsMobile();
  const sectionRef = useRef(null);

  // Desktop: inline accordion
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  // Mobile: bottom sheet
  const [sheetExp, setSheetExp] = useState(null);
  const openSheet  = useCallback((exp) => setSheetExp(exp), []);
  const closeSheet = useCallback(() => setSheetExp(null), []);

  const handleTap = useCallback((exp, i) => {
    if (!exp.preview && !exp.previewUrl) return; // no preview — nothing to show
    if (isMobile) {
      openSheet(exp);
    } else {
      toggle(i);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, openSheet, openIndex]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        EXPERIENCES.forEach(warmupPreview);
        observer.disconnect();
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="experience"
      className="min-h-screen px-4 sm:px-8 md:px-12 lg:px-20 xl:px-28
        pt-16 pb-20 sm:pt-20 md:pt-16 lg:pt-20"
    >
      {/* Sticky title */}
      <div className="sticky top-0 z-20 bg-lightBeige pt-16 pb-3 transition-shadow duration-300 -mx-4 sm:-mx-8 md:-mx-12 px-4 sm:px-8 md:px-12 mb-8 sm:mb-10 md:mb-14 text-center">
        <h2 className="font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
          <ColoredTitle text="Experience." />
        </h2>
      </div>

      {/* Divider */}
      <div className="flex justify-center mb-8 sm:mb-10 md:mb-14">
        <div className="h-[3px] bg-lightBrown rounded-full w-28 sm:w-36 md:w-48 lg:w-64"></div>
      </div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="flex flex-col gap-0"
      >
        {EXPERIENCES.map((exp, i) => {
          const isOpen = !isMobile && openIndex === i;
          const hasPreview = !!(exp.preview || exp.previewUrl);

          return (
            <div key={i} className="flex flex-col sm:flex-row gap-0">
              {/* Left: date + vertical line */}
              <div className="flex sm:flex-col sm:items-center sm:w-48 md:w-56 lg:w-64 shrink-0">
                <div className="flex items-center gap-2 sm:gap-0 sm:flex-col sm:items-center">
                  <span className="text-lightBrown text-base sm:text-lg shrink-0">
                    ✶
                  </span>
                  <p className="text-xs sm:text-sm font-semibold text-midBrown whitespace-nowrap sm:mt-1 ml-2 sm:ml-0">
                    {exp.period}
                  </p>
                </div>
                {i < EXPERIENCES.length - 1 && (
                  <div className="hidden sm:block w-[2px] flex-1 bg-lightBrown/40 mx-auto mt-2 min-h-[8rem]"></div>
                )}
              </div>

              {/* Right: content area */}
              <div className="pl-6 sm:pl-8 md:pl-10 pb-14 sm:pb-16 border-l-2 border-lightBrown/40 sm:border-none mt-1 sm:mt-0 flex-1 min-w-0">
                {/* Row: text + preview side by side when open (desktop only) */}
                <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                  {/* Text block — no layout animation on mobile */}
                  <motion.div
                    {...(!isMobile && { layout: true })}
                    transition={{ duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className={
                      isOpen && hasPreview ? "md:w-2/5 shrink-0" : "w-full"
                    }
                  >
                    {/* Clickable header */}
                    <button
                      onMouseEnter={() => warmupPreview(exp)}
                      onFocus={() => warmupPreview(exp)}
                      onClick={() => hasPreview && handleTap(exp, i)}
                      className={`text-left w-full group ${hasPreview ? "cursor-pointer" : "cursor-default"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {/* Title with animated underline on hover */}
                        <motion.h3
                          {...(!isMobile && { layout: true })}
                          transition={{
                            duration: 0.65,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                          className={`font-bold transition-all duration-300 relative
                            ${
                              isOpen
                                ? "text-lg sm:text-xl md:text-2xl"
                                : "text-xl sm:text-2xl md:text-3xl lg:text-4xl"
                            }`}
                        >
                          {exp.title}
                          {/* Desktop: underline slides in on hover */}
                          {hasPreview && !isOpen && !isMobile && (
                            <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-lightBrown rounded-full transition-all duration-300 group-hover:w-full" />
                          )}
                        </motion.h3>

                        {/* Desktop keeps the star; mobile shows a small "view" pill */}
                        {hasPreview && (
                          isMobile ? (
                            <span
                              className="ml-2 inline-flex h-6 shrink-0 items-center justify-center
                                rounded-full border border-lightBrown/50 bg-lightBrown/10
                                px-2.5 text-[10px] font-semibold leading-none
                                tracking-[0.12em] text-lightBrown shadow-sm"
                            >
                              View
                            </span>
                          ) : (
                            <motion.span
                              animate={{
                                rotate: isOpen ? 720 : 0,
                                scale: isOpen ? 1.8 : 1,
                              }}
                              whileHover={!isOpen ? { rotate: 90, scale: 1.6 } : {}}
                              transition={
                                isOpen
                                  ? { duration: 0.7, ease: [0.2, 0.8, 0.4, 1] }
                                  : {
                                      rotate: { duration: 0 },
                                      scale: { duration: 0.3, ease: "easeOut" },
                                    }
                              }
                              className="text-lightBrown shrink-0 mt-0.5 inline-block"
                              style={{ fontSize: "1.1rem" }}
                            >
                              ✶
                            </motion.span>
                          )
                        )}
                      </div>
                      <p
                        className={`font-semibold italic text-brown mb-2 sm:mb-3 transition-all duration-300
                        ${isOpen ? "text-sm sm:text-base" : "text-base sm:text-lg md:text-xl"}`}
                      >
                        {exp.company}
                      </p>
                    </button>

                    <AnimatePresence initial={false}>
                      {(!isOpen || !hasPreview) && (
                        <motion.p
                          key="desc"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{
                            duration: 0.55,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                          className="leading-relaxed text-midBrown overflow-hidden
                            text-sm sm:text-base md:text-base lg:text-lg max-w-2xl"
                        >
                          {exp.description}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Preview — slides in when open */}
                  <AnimatePresence>
                    {isOpen && hasPreview && (
                      <motion.div
                        key="preview"
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 24 }}
                        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.15 }}
                        className="flex-1 min-w-0"
                      >
                        {exp.preview ? (
                          <GifPreview src={exp.preview} alt={exp.previewAlt} />
                        ) : exp.previewUrl ? (
                          <IframePreview url={exp.previewUrl} title={exp.company} />
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          );
        })}
      </motion.div>

      {/* Mobile bottom sheet */}
      <BottomSheet exp={sheetExp} onClose={closeSheet} />
    </section>
  );
}
